import React, { useState, useEffect } from "react";
import { FaSun, FaMoon, FaMars, FaVenus } from "react-icons/fa";
import { motion } from "framer-motion";
import CustomDropdown from '../common/UserCustomDropdown'; 
import api from "../../services/api";

/* ===================== CONSTANTS ===================== */
const NAKSHATRAS = [
  "அஸ்வினி (Ashwini)",
  "பரணி (Bharani)",
  "கிருத்திகை (Krittika)",
  "ரோகிணி (Rohini)",
  "மிருகசீரிஷம் (Mrigashirsha)",
  "திருவாதிரை (Ardra)",
  "புனர்பூசம் (Punarvasu)",
  "பூசம் (Pushya)",
  "ஆயில்யம் (Ashlesha)",
  "மகம் (Magha)",
  "பூரம் (Purva Phalguni)",
  "உத்திரம் (Uttara Phalguni)",
  "அஸ்தம் (Hasta)",
  "சித்திரை (Chitra)",
  "சுவாதி (Swati)",
  "விசாகம் (Vishakha)",
  "அனுஷம் (Anuradha)",
  "கேட்டை (Jyeshtha)",
  "மூலம் (Mula)",
  "பூராடம் (Purva Ashadha)",
  "உத்திராடம் (Uttara Ashadha)",
  "திருவோணம் (Shravana)",
  "அவிட்டம் (Dhanishta)",
  "சதயம் (Shatabhisha)",
  "பூரட்டாதி (Purva Bhadrapada)",
  "உத்திரட்டாதி (Uttara Bhadrapada)",
  "ரேவதி (Revati)",
];

const RASIS = [
  "மேஷம் (Aries)",
  "ரிஷபம் (Taurus)",
  "மிதுனம் (Gemini)",
  "கடகம் (Cancer)",
  "சிம்மம் (Leo)",
  "கன்னி (Virgo)",
  "துலாம் (Libra)",
  "விருச்சிகம் (Scorpio)",
  "தனுசு (Sagittarius)",
  "மகரம் (Capricorn)",
  "கும்பம் (Aquarius)",
  "மீனம் (Pisces)",
];

// Convert to options format required by CustomDropdown
const nakshatraOptions = NAKSHATRAS.map((name, index) => ({
  value: index + 1,
  label: name,
}));

const rasiOptions = RASIS.map((name, index) => ({
  value: index + 1,
  label: name,
}));

/* ===================== DATA FOR REAL CALCULATION ===================== */
// Rajju mapping
const rajjuGroups = [
  "Paada", "Thodai", "Udara", "Kanda", "Sirsu", "Kanda", "Udara", "Thodai", "Paada",
  "Paada", "Thodai", "Udara", "Kanda", "Sirsu", "Kanda", "Udara", "Thodai", "Paada",
  "Paada", "Thodai", "Udara", "Kanda", "Sirsu", "Kanda", "Udara", "Thodai", "Paada"
];

// Vedha pairs
const vedhaPairs = [
  [1, 18], [2, 17], [3, 16], [4, 15], [5, 14], [6, 22], [7, 21], [8, 20], [9, 19],
  [10, 27], [11, 26], [12, 25], [13, 24]
];

// Yoni (Animal) mapping
const yoniAnimals = [
  "Horse", "Elephant", "Sheep", "Serpent", "Serpent", "Dog", "Cat", "Sheep", "Cat",
  "Rat", "Rat", "Cow", "Buffalo", "Tiger", "Buffalo", "Tiger", "Deer", "Deer",
  "Dog", "Monkey", "Mongoose", "Monkey", "Lion", "Horse", "Lion", "Cow", "Elephant"
];

const enemyAnimals = {
  "Horse": "Buffalo", "Buffalo": "Horse",
  "Elephant": "Lion", "Lion": "Elephant",
  "Sheep": "Monkey", "Monkey": "Sheep",
  "Serpent": "Mongoose", "Mongoose": "Serpent",
  "Dog": "Deer", "Deer": "Dog",
  "Cat": "Rat", "Rat": "Cat",
  "Cow": "Tiger", "Tiger": "Cow"
};

// Gana mapping
const ganas = [
  "Deva", "Manushya", "Rakshasa", "Manushya", "Deva", "Manushya", "Deva", "Deva", "Rakshasa",
  "Rakshasa", "Manushya", "Manushya", "Deva", "Rakshasa", "Deva", "Rakshasa", "Deva", "Rakshasa",
  "Rakshasa", "Manushya", "Manushya", "Deva", "Rakshasa", "Rakshasa", "Manushya", "Manushya", "Deva"
];

// Rasi Lords & Friendship
const rasiLords = ["Mars", "Venus", "Mercury", "Moon", "Sun", "Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Saturn", "Jupiter"];
const planetFriends = {
  Sun: { friend: ["Moon", "Mars", "Jupiter"], neutral: ["Mercury"], enemy: ["Venus", "Saturn"] },
  Moon: { friend: ["Sun", "Mercury"], neutral: ["Mars", "Jupiter", "Venus", "Saturn"], enemy: [] },
  Mars: { friend: ["Sun", "Moon", "Jupiter"], neutral: ["Venus", "Saturn"], enemy: ["Mercury"] },
  Mercury: { friend: ["Sun", "Venus"], neutral: ["Mars", "Jupiter", "Saturn"], enemy: ["Moon"] },
  Jupiter: { friend: ["Sun", "Moon", "Mars"], neutral: ["Saturn"], enemy: ["Mercury", "Venus"] },
  Venus: { friend: ["Mercury", "Saturn"], neutral: ["Mars", "Jupiter"], enemy: ["Sun", "Moon"] },
  Saturn: { friend: ["Mercury", "Venus"], neutral: ["Jupiter"], enemy: ["Sun", "Moon", "Mars"] },
};

// Vasiya specific mappings (if Boy is in this Rasi, Girl should be in array)
const vasiya = {
  1: [5, 8], 2: [4, 7], 3: [6], 4: [8, 9], 5: [7], 6: [3, 12],
  7: [6, 10], 8: [4, 6], 9: [12], 10: [1, 11], 11: [1], 12: [10]
};

/* ===================== REAL CALCULATION FUNCTION ===================== */
const calculate10Porutham = (boyN, boyR, girlN, girlR) => {
  let score = 0;
  const poruthams = [];

  // 1. Dina Porutham
  let countGtoB = ((boyN - girlN + 27) % 27) + 1;
  let dinaMatched = [2, 4, 6, 8, 9, 11, 13, 15, 18, 20, 24, 26].includes(countGtoB) || countGtoB === 1;
  if (dinaMatched) score++;
  poruthams.push({ name: "Dina Porutham", tamil: "தின பொருத்தம்", matched: dinaMatched, desc: "Health & Prosperity" });

  // 2. Gana Porutham
  const bGana = ganas[boyN - 1];
  const gGana = ganas[girlN - 1];
  let ganaMatched = false;
  if (bGana === gGana) ganaMatched = true;
  else if (bGana === "Deva" && gGana === "Manushya") ganaMatched = true;
  else if (gGana === "Deva" && bGana === "Manushya") ganaMatched = true; 
  if (ganaMatched) score++;
  poruthams.push({ name: "Gana Porutham", tamil: "கண பொருத்தம்", matched: ganaMatched, desc: "Temperament" });

  // 3. Mahendra Porutham
  let mahendraMatched = [4, 7, 10, 13, 16, 19, 22, 25].includes(countGtoB);
  if (mahendraMatched) score++;
  poruthams.push({ name: "Mahendra Porutham", tamil: "மகேந்திர பொருத்தம்", matched: mahendraMatched, desc: "Wealth & Progeny" });

  // 4. Stree Dheergha Porutham
  let streeMatched = countGtoB > 13;
  if (streeMatched) score++;
  poruthams.push({ name: "Stree Dheergha Porutham", tamil: "ஸ்திரீ தீர்க்க பொருத்தம்", matched: streeMatched, desc: "Longevity & Prosperity" });

  // 5. Yoni Porutham
  const bYoni = yoniAnimals[boyN - 1];
  const gYoni = yoniAnimals[girlN - 1];
  let yoniMatched = enemyAnimals[bYoni] !== gYoni;
  if (yoniMatched) score++;
  poruthams.push({ name: "Yoni Porutham", tamil: "யோனி பொருத்தம்", matched: yoniMatched, desc: "Sexual Compatibility" });

  // 6. Rasi Porutham
  let rasiCountGtoB = ((boyR - girlR + 12) % 12) + 1;
  let rasiMatched = rasiCountGtoB > 6 || rasiCountGtoB === 1;
  if (rasiMatched) score++;
  poruthams.push({ name: "Rasi Porutham", tamil: "ராசி பொருத்தம்", matched: rasiMatched, desc: "Continuation of Progeny" });

  // 7. Rasi Athipathi Porutham
  const bLord = rasiLords[boyR - 1];
  const gLord = rasiLords[girlR - 1];
  let lordMatched = false;
  if (bLord === gLord) lordMatched = true;
  else {
    const isFriendB = planetFriends[bLord].friend.includes(gLord);
    const isFriendG = planetFriends[gLord].friend.includes(bLord);
    const isNeutralB = planetFriends[bLord].neutral.includes(gLord);
    const isNeutralG = planetFriends[gLord].neutral.includes(bLord);
    if (isFriendB && isFriendG) lordMatched = true;
    else if ((isFriendB && isNeutralG) || (isNeutralB && isFriendG)) lordMatched = true;
    else if (isNeutralB && isNeutralG) lordMatched = true; 
  }
  if (lordMatched) score++;
  poruthams.push({ name: "Rasi Athipathi Porutham", tamil: "ராசி அதிபதி பொருத்தம்", matched: lordMatched, desc: "Friendship between Rasi Lords" });

  // 8. Vasiya Porutham
  let vasiyaMatched = vasiya[boyR]?.includes(girlR) || vasiya[girlR]?.includes(boyR) || false;
  if (vasiyaMatched) score++;
  poruthams.push({ name: "Vasiya Porutham", tamil: "வசிய பொருத்தம்", matched: vasiyaMatched, desc: "Mutual Attraction" });

  // 9. Rajju Porutham
  const bRajju = rajjuGroups[boyN - 1];
  const gRajju = rajjuGroups[girlN - 1];
  let rajjuMatched = bRajju !== gRajju;
  if (rajjuMatched) score++;
  poruthams.push({ name: "Rajju Porutham", tamil: "ரஜ்ஜு பொருத்தம்", matched: rajjuMatched, desc: "Longevity of Husband" });

  // 10. Vedha Porutham
  let vedhaMatched = true;
  for(let pair of vedhaPairs) {
    if ((pair[0] === boyN && pair[1] === girlN) || (pair[0] === girlN && pair[1] === boyN)) {
      vedhaMatched = false;
      break;
    }
  }
  if (vedhaMatched) score++;
  poruthams.push({ name: "Vedha Porutham", tamil: "வேதை பொருத்தம்", matched: vedhaMatched, desc: "Absence of Mutual Affliction" });

  // Verdict
  let status, verdict, details;
  if (!rajjuMatched) {
    status = "Not Recommended";
    verdict = "Not Recommended";
    details = "Rajju Porutham is missing, which is considered highly critical and inauspicious.";
  } else if (score >= 8) {
    status = "Excellent Match";
    verdict = "Perfect Match";
    details = "Exceptional compatibility! 8 or more poruthams matched.";
  } else if (score >= 6) {
    status = "Good Match";
    verdict = "Good Match";
    details = "Strong harmony. Good overall compatibility.";
  } else if (score >= 5) {
    status = "Average Match";
    verdict = "Average Match";
    details = "Moderate compatibility; acceptable for marriage.";
  } else {
    status = "Below Average";
    verdict = "Not Recommended";
    details = "Less than 5 poruthams matched. Significant challenges.";
  }

  return {
    score,
    total: 10,
    status,
    verdict,
    details,
    poruthams
  };
};

/* ===================== COMPONENT ===================== */
const HoroscopeMatch = () => {
  const [boyName, setBoyName] = useState("");
  const [boyNakshatra, setBoyNakshatra] = useState(null);
  const [boyRasi, setBoyRasi] = useState(null);
  const [boyPlaceOfBirth, setBoyPlaceOfBirth] = useState("");

  const [girlName, setGirlName] = useState("");
  const [girlNakshatra, setGirlNakshatra] = useState(null);
  const [girlRasi, setGirlRasi] = useState(null);
  const [girlPlaceOfBirth, setGirlPlaceOfBirth] = useState("");

  const [matchResult, setMatchResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get("/dashboard/my-profile");
        const profile = response.data.profile;
        const gender = profile.basicInfo?.gender;
        const fullName = profile.basicInfo?.firstName || "";
        const star = profile.horoscope?.star || "";
        const rasi = profile.horoscope?.rasi || "";
        const birthPlace = profile.horoscope?.birthPlace || "";

        // Helper to find option even if it's bilingual
        const findOption = (options, str) => {
          if (!str) return null;
          const cleanStr = str.toLowerCase();
          return options.find(o => {
            const oClean = o.label.toLowerCase();
            const words = cleanStr.match(/[a-z]+/g) || [];
            // if any english word in the db string matches the option label
            return words.some(w => w.length > 3 && oClean.includes(w)) || 
                   oClean.includes(cleanStr) || cleanStr.includes(oClean);
          })?.value;
        };

        const foundStar = findOption(nakshatraOptions, star);
        const foundRasi = findOption(rasiOptions, rasi);

        if (gender === "Male") {
          setBoyName(fullName);
          if (foundStar) setBoyNakshatra(foundStar);
          if (foundRasi) setBoyRasi(foundRasi);
          setBoyPlaceOfBirth(birthPlace);
        } else if (gender === "Female") {
          setGirlName(fullName);
          if (foundStar) setGirlNakshatra(foundStar);
          if (foundRasi) setGirlRasi(foundRasi);
          setGirlPlaceOfBirth(birthPlace);
        }
      } catch (err) {
        console.error("Failed to load user profile for auto-fill", err);
      }
    };
    fetchProfile();
  }, []);

  const handleMatch = (e) => {
    e.preventDefault();

    if (!boyNakshatra || !boyRasi || !girlNakshatra || !girlRasi) {
      alert("Please select Nakshatra and Rasi for both partners.");
      return;
    }

    setLoading(true);

    setTimeout(() => {
      const result = calculate10Porutham(
        boyNakshatra,
        boyRasi,
        girlNakshatra,
        girlRasi
      );
      setMatchResult(result);
      setLoading(false);
    }, 1500);
  };

  return (
    <div className="min-h-full bg-transparent p-4 lg:p-8">
      <div className="w-full mx-auto">
        <h2 className="text-3xl font-bold text-text-primary flex items-center gap-3 mb-6">
          <FaSun className="text-orange-500" /> Horoscope Match (Gun Milan)
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* ================= FORM ================= */}
          <div className="lg:col-span-5 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary rounded-3xl shadow-xl p-6">
            <form onSubmit={handleMatch} className="space-y-8">
              {/* BOY */}
              <div className="border border-glass-border rounded-2xl p-6 bg-[rgba(59,130,246,0.05)] dark:bg-[rgba(59,130,246,0.1)]">
                <h3 className="font-bold text-blue-400 flex items-center gap-2 mb-4">
                  <FaMars /> Boy's Details
                </h3>

                <input
                  type="text"
                  placeholder="Name (Optional)"
                  value={boyName}
                  onChange={(e) => setBoyName(e.target.value)}
                  className="w-full mb-5 p-3 bg-transparent dark:bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-text-primary dark:text-white placeholder-text-muted"
                />

                <div className="space-y-4">
                  <CustomDropdown
                    options={nakshatraOptions}
                    value={boyNakshatra}
                    onChange={setBoyNakshatra}
                    placeholder="Select Nakshatra"
                    searchable={true}
                  />

                  <CustomDropdown
                    options={rasiOptions}
                    value={boyRasi}
                    onChange={setBoyRasi}
                    placeholder="Select Rasi (Moon Sign)"
                    searchable={true}
                  />

                  <input
                    type="text"
                    placeholder="Place of Birth"
                    value={boyPlaceOfBirth}
                    onChange={(e) => setBoyPlaceOfBirth(e.target.value)}
                    className="w-full p-3 bg-transparent dark:bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-text-primary dark:text-white placeholder-text-muted"
                  />
                </div>
              </div>

              {/* GIRL */}
              <div className="border border-glass-border rounded-2xl p-6 bg-pink-50/50 dark:bg-pink-900/10">
                <h3 className="font-bold text-pink-600 flex items-center gap-2 mb-4">
                  <FaVenus /> Girl's Details
                </h3>

                <input
                  type="text"
                  placeholder="Name (Optional)"
                  value={girlName}
                  onChange={(e) => setGirlName(e.target.value)}
                  className="w-full mb-5 p-3 bg-transparent dark:bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 text-text-primary dark:text-white placeholder-text-muted"
                />

                <div className="space-y-4">
                  <CustomDropdown
                    options={nakshatraOptions}
                    value={girlNakshatra}
                    onChange={setGirlNakshatra}
                    placeholder="Select Nakshatra"
                    searchable={true}
                  />

                  <CustomDropdown
                    options={rasiOptions}
                    value={girlRasi}
                    onChange={setGirlRasi}
                    placeholder="Select Rasi (Moon Sign)"
                    searchable={true}
                  />

                  <input
                    type="text"
                    placeholder="Place of Birth"
                    value={girlPlaceOfBirth}
                    onChange={(e) => setGirlPlaceOfBirth(e.target.value)}
                    className="w-full p-3 bg-transparent dark:bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 text-text-primary dark:text-white placeholder-text-muted"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mx-auto block w-64 bg-primary-pink shadow-[0_0_15px_rgba(255,46,136,0.4)] hover:bg-rose-800 disabled:opacity-70 text-white py-4 rounded-xl font-bold transition-colors"
              >
                {loading
                  ? "Calculating Compatibility..."
                  : "Calculate Compatibility"}
              </button>
            </form>
          </div>

          {/* ================= RESULT ================= */}
          <div className="lg:col-span-7">
            {matchResult ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary rounded-3xl shadow-xl p-8 h-full"
              >
                <div className="text-center mb-8">
                  <h3 className="text-4xl font-bold text-text-primary mb-2">
                    {matchResult.verdict}
                  </h3>
                  <p className="text-2xl text-green-600 font-semibold">
                    {matchResult.score} / {matchResult.total} Poruthams Matched
                  </p>
                  <p className="text-text-secondary mt-2">{matchResult.details}</p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {matchResult.poruthams.map((p) => (
                    <div
                      key={p.name}
                      className="p-5 border border-glass-border rounded-2xl text-center flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-white dark:from-[rgba(255,255,255,0.05)] dark:to-transparent"
                    >
                      <div className="font-semibold text-text-secondary text-sm">
                        {p.name}
                      </div>
                      <div className="font-bold text-primary-pink text-xs mt-1">
                        ({p.tamil})
                      </div>
                      <div className="text-4xl font-black drop-shadow-sm mt-3 mb-1">
                        {p.matched ? (
                          <span className="text-green-500">✔</span>
                        ) : (
                          <span className="text-red-500">✖</span>
                        )}
                      </div>
                      <div className="text-xs text-text-muted mt-2">{p.desc}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary rounded-3xl border-2 border-dashed border-glass-border p-12 text-center">
                <FaMoon className="text-4xl text-gray-200 mb-6" />
                <p className="text-text-muted text-lg">
                  Fill in the details and click calculate to see the
                  compatibility result
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoroscopeMatch;
